import React, { useRef } from 'react';
import { Controller, useFormContext, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  StyledWrapInput,
  StyledLabel,
  StyledInput,
  ErrorContainer,
  Error,
} from 'components/theme/Form';
import { validateEmpty } from 'utils/validators';

export interface ITextFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  rules?: RegisterOptions;
}

const TextFieldInput = ({
  name,
  label,
  required,
  placeholder,
  autoComplete,
  ...props
}: ITextFieldProps) => {
  const { control, trigger } = useFormContext();
  const { t: translate } = useTranslation();
  const inputId = `input-${name.replace('.', '-')}`;
  const errorRef = useRef<HTMLDivElement>(null);

  return (
    <Controller
      name={name}
      control={control}
      rules={required ? validateEmpty() : props.rules ?? {}}
      {...props}
      render={({ field, fieldState }) => {
        const errorMessage = fieldState.error?.message as string;
        const errorId = `error-${inputId}`;

        return (
          <StyledWrapInput>
            <StyledLabel htmlFor={inputId}>
              {label}
              {required && <span aria-hidden="true">*</span>}
            </StyledLabel>
            <StyledInput
              required={required}
              type="text"
              id={inputId}
              aria-required={required ? 'true' : 'false'}
              aria-invalid={!!errorMessage}
              aria-describedby={errorMessage ? errorId : undefined}
              placeholder={placeholder}
              autoComplete={autoComplete ?? 'off'}
              {...field}
              onBlur={() => {
                field.onBlur();
                trigger(name);
              }}
            />
            {errorMessage && (
              <ErrorContainer
                ref={errorRef}
                role="alert"
                aria-live="assertive"
                tabIndex={-1}
              >
                <Error id={errorId}>{translate(errorMessage)}</Error>
              </ErrorContainer>
            )}
          </StyledWrapInput>
        );
      }}
    />
  );
};

export default TextFieldInput;
